import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Building2, Store, Maximize, Users,
    TrendingUp, TrendingDown,
    Coffee, Clock,
    LayoutDashboard, LogOut, X, ExternalLink,
    Sparkles, Lightbulb, ArrowUpRight, ArrowDownRight, AlertTriangle, Activity,
} from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import { ROVER_URL, openExternalApp } from '../Shared/externalApps';
import { useAuth } from '../hooks/useAuth';
import PropertyFilter from '../Shared/PropertyFilter';
import { formatMoney, formatNumber, formatPct, formatCompactNumber } from '../Shared/Formatters';
import egoalLogo from '../../../assets/images/logo/egoallogo.png';
import './spatial-landing.css';

const FRAME_NAMES = [
    'frame_0001_0.60s.webp', 'frame_0002_0.90s.webp', 'frame_0003_1.20s.webp',
    'frame_0004_1.50s.webp', 'frame_0005_1.80s.webp', 'frame_0006_2.10s.webp',
    'frame_0007_2.40s.webp', 'frame_0008_2.70s.webp', 'frame_0009_3.00s.webp',
    'frame_0010_3.30s.webp', 'frame_0011_3.60s.webp', 'frame_0012_3.90s.webp',
    'frame_0013_4.20s.webp', 'frame_0014_4.50s.webp', 'frame_0015_4.80s.webp',
    'frame_0016_5.10s.webp', 'frame_0017_5.40s.webp', 'frame_0018_5.70s.webp',
    'frame_0019_6.00s.webp', 'frame_0020_6.30s.webp', 'frame_0021_6.60s.webp',
    'frame_0022_6.90s.webp', 'frame_0023_7.20s.webp', 'frame_0024_7.50s.webp',
    'frame_0025_7.80s.webp',
];
const TOTAL_FRAMES = FRAME_NAMES.length;

const PLACEHOLDER = '—';

export default function SpatialLandingPage() {
    const { t } = useTranslation();
    const { concessionaireId, concessionaireName, propertyAccess, logout } = useAuth();
    const navigate = useNavigate();
    const [brief, setBrief] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [imagesLoaded, setImagesLoaded] = useState(0);
    const [salesPopup, setSalesPopup] = useState(false);
    const [categoryPopup, setCategoryPopup] = useState(false);
    const [footfallPopup, setFootfallPopup] = useState(false);
    const [riskPopup, setRiskPopup] = useState(false);

    // Note: body.v3-fullbleed (hides sidebar + reclaims margin) is now toggled
    // by Layout.jsx based on URL — page transitions overlap the old/new page
    // mount, so a page-level useEffect ran too late and let the sidebar flash
    // when navigating back to this route.

    // Keyboard escape — Esc or D jumps to the smart dashboard.
    useEffect(() => {
        const onKey = (e) => {
            // Ignore when the user is typing in an input/textarea
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
            if (e.key === 'Escape' || e.key === 'd' || e.key === 'D') {
                navigate(`${process.env.PUBLIC_URL}/v3/dashboard`);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navigate]);

    const [selectedProperties, setSelectedProperties] = useState([]);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const overlayRef = useRef(null);
    // True while the intro auto-play owns `scrollProgress`. The scroll listener
    // checks this and stays out of the way so the two don't fight.
    const autoPlayActiveRef = useRef(false);
    const images = useMemo(() => [], []);

    // Fetch brief for the active property
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await v3Api.spatial.brief({
                    concessionaire_id: concessionaireId,
                    property_id: selectedProperties.length > 0 ? selectedProperties.join(',') : propertyAccess,
                });
                if (!cancelled) setBrief(res.data);
            } catch (err) {
                if (!cancelled) setLoadError(err.response?.data?.error || err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [concessionaireId, propertyAccess, selectedProperties]);

    // Preload mall frames
    useEffect(() => {
        let loaded = 0;
        FRAME_NAMES.forEach((name, idx) => {
            const img = new Image();
            img.src = `${process.env.PUBLIC_URL}/mall-sequence/${name}`;
            img.onload = () => {
                loaded += 1; setImagesLoaded(loaded);
                // Paint the opening frame the instant it decodes so the stage
                // isn't left black during the multi-MB preload. Don't gate the
                // first paint on the exact tick the counter tops out — the canvas
                // can mount a frame late (StrictMode remount / slow final frame),
                // and that race left the canvas blank with the title already shown.
                if (idx === 0) drawFrame(0);
            };
            img.onerror = () => { loaded += 1; setImagesLoaded(loaded); };
            images[idx] = img;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Safety net: (re)draw the frame for the current scroll position whenever the
    // load counter advances or the canvas (re)mounts. Decouples the first paint
    // from the fragile "loaded === TOTAL_FRAMES && canvasRef.current" condition
    // so the mall never renders blank once any frame is available.
    useEffect(() => {
        if (imagesLoaded > 0 && canvasRef.current) {
            const idx = Math.min(TOTAL_FRAMES - 1, Math.round(scrollProgress * (TOTAL_FRAMES - 1)));
            drawFrame(idx);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagesLoaded]);

    function drawFrame(index) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const img = images[index];
        if (!img || !img.complete || !img.naturalWidth) return;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        const iW = img.naturalWidth, iH = img.naturalHeight;
        const scale = Math.max(canvas.width / iW, canvas.height / iH);
        const x = (canvas.width - iW * scale) / 2;
        const y = (canvas.height - iH * scale) / 2;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.filter = 'brightness(1.08) contrast(1.04)';
        ctx.drawImage(img, x, y, iW * scale, iH * scale);
        ctx.filter = 'none';
    }

    // Scroll-driven frame index — pure CSS sticky + manual scroll handler.
    // No GSAP DOM mutation; React stays in full control of the tree, so navigating
    // away via the side menu doesn't leave dangling spacer divs that break
    // React's removeChild during unmount.
    const [scrollProgress, setScrollProgress] = useState(0);
    useEffect(() => {
        if (imagesLoaded < TOTAL_FRAMES) return;

        const onScroll = () => {
            if (autoPlayActiveRef.current) return; // auto-play owns progress; don't fight it
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            // total scrollable distance inside this section = section height − viewport height
            const totalScrollable = Math.max(1, el.offsetHeight - window.innerHeight);
            const scrolled = Math.min(totalScrollable, Math.max(0, -rect.top));
            const progress = scrolled / totalScrollable;
            setScrollProgress(progress);
            const frameIndex = Math.min(TOTAL_FRAMES - 1, Math.round(progress * (TOTAL_FRAMES - 1)));
            drawFrame(frameIndex);
        };

        const onResize = () => {
            const idx = Math.min(TOTAL_FRAMES - 1, Math.round(scrollProgress * (TOTAL_FRAMES - 1)));
            drawFrame(idx);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        onScroll(); // prime first frame at current scroll position

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagesLoaded]);

    // Two-phase reveal:
    //   `revealed`       → title bar + top-right actions, as soon as frames decode (orientation cues)
    //   `cardsRevealed`  → bento overlay (stats, columns, bottom logo), once user scrolls past ~50%
    const [revealed, setRevealed] = useState(false);
    useEffect(() => {
        if (imagesLoaded >= TOTAL_FRAMES) {
            const t = setTimeout(() => setRevealed(true), 120);
            return () => clearTimeout(t);
        }
    }, [imagesLoaded]);
    const cardsRevealed = revealed && scrollProgress >= 0.5;

    const pct = Math.round((imagesLoaded / TOTAL_FRAMES) * 100);

    // Loader timing — keep the EGoal logo on screen for a beat (Gmail-style) even
    // if frames decode instantly, then fade out smoothly. `minTimeUp` guarantees a
    // ~1.8s minimum; `loaderGone` unmounts the node after the fade completes.
    const [minTimeUp, setMinTimeUp] = useState(false);
    const [loaderGone, setLoaderGone] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setMinTimeUp(true), 1800);
        return () => clearTimeout(t);
    }, []);
    const loaderReady = imagesLoaded >= TOTAL_FRAMES && minTimeUp;
    useEffect(() => {
        if (loaderReady) {
            const t = setTimeout(() => setLoaderGone(true), 560); // after fade-out
            return () => clearTimeout(t);
        }
    }, [loaderReady]);

    // Auto-play: once the scene is shown, animate the mall sequence + reveal on its
    // own so the user never has to scroll. Driven by a requestAnimationFrame loop
    // that paints frames + advances progress DIRECTLY (no dependency on the scroll
    // container) and best-effort-syncs the real scroll position. Any real user
    // input (wheel / touch / scroll keys) instantly cancels it.
    useEffect(() => {
        if (!loaderGone) return undefined;
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return undefined;

        const DURATION = 9000; // ~9s full sweep — unhurried, cinematic
        // Stall guard: never advance more than ~50ms of progress in a single tick.
        // A main-thread hitch (first-render commit, brief data landing) otherwise
        // lets wall-clock time run ahead, so when rAF resumes the sequence teleports
        // forward — the "stuck, then very fast" jump. Accumulating clamped deltas
        // instead means a hitch just pauses the sweep and it resumes smoothly.
        const MAX_FRAME_MS = 50;
        const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

        let rafId = 0;
        let lastTs = null;
        let progress = 0; // linear 0..1, accumulated from clamped frame deltas
        let cancelled = false;
        autoPlayActiveRef.current = true;

        // Move the real scroller to a position ONCE — at hand-off (user takes over,
        // or the sweep completes). NOT per frame: a per-frame window.scrollTo forces
        // a synchronous reflow AND re-fires the scroll listener, which then
        // double-drives progress. That feedback + layout thrash was the stutter.
        // `pos` is the LINEAR scroll fraction; the scroll listener maps scroll→frame
        // linearly, so we hand off at the eased value to keep the frame identical.
        const syncRealScroll = (pos) => {
            const el = containerRef.current;
            if (!el) return;
            const max = Math.max(0, el.offsetHeight - window.innerHeight);
            const pb = document.querySelector('.page-body');
            // The app sets html { scroll-behavior: smooth }, so a plain scrollTo
            // here would ANIMATE the hand-off — and the (now live) scroll listener
            // would repaint frames along that glide, snapping back to a mid frame
            // then racing to the last. Force this one jump instant by overriding
            // scroll-behavior on the actual scrollers, then restore it.
            const de = document.documentElement;
            const prevDe = de.style.scrollBehavior;
            const prevPb = pb ? pb.style.scrollBehavior : '';
            de.style.scrollBehavior = 'auto';
            if (pb) pb.style.scrollBehavior = 'auto';
            window.scrollTo(0, max * pos);
            if (pb) pb.scrollTop = Math.max(0, pb.scrollHeight - pb.clientHeight) * pos;
            de.style.scrollBehavior = prevDe;
            if (pb) pb.style.scrollBehavior = prevPb;
        };

        const step = (ts) => {
            if (cancelled) return;
            if (lastTs == null) lastTs = ts;
            const dt = Math.min(MAX_FRAME_MS, ts - lastTs);
            lastTs = ts;
            progress = Math.min(1, progress + dt / DURATION);
            const eased = easeInOut(progress);
            drawFrame(Math.min(TOTAL_FRAMES - 1, Math.round(eased * (TOTAL_FRAMES - 1))));
            setScrollProgress(eased);
            if (progress < 1) {
                rafId = window.requestAnimationFrame(step);
            } else {
                autoPlayActiveRef.current = false;
                syncRealScroll(1); // land the scroller at the end for a seamless hand-off
            }
        };

        const cancel = () => {
            if (cancelled) return;
            cancelled = true;
            autoPlayActiveRef.current = false;
            if (rafId) window.cancelAnimationFrame(rafId);
            // Match the real scroll to where the animation is (eased) so the user's
            // own scroll continues from the current point instead of snapping back.
            syncRealScroll(easeInOut(progress));
            teardown();
        };
        const onKey = (e) => {
            if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar'].includes(e.key)) cancel();
        };
        function teardown() {
            window.removeEventListener('wheel', cancel);
            window.removeEventListener('touchstart', cancel);
            window.removeEventListener('keydown', onKey);
        }
        window.addEventListener('wheel', cancel, { passive: true });
        window.addEventListener('touchstart', cancel, { passive: true });
        window.addEventListener('keydown', onKey);

        rafId = window.requestAnimationFrame(step);

        return () => {
            cancelled = true;
            autoPlayActiveRef.current = false;
            if (rafId) window.cancelAnimationFrame(rafId);
            teardown();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaderGone]);

    const data = deriveData(brief, t);
    const propertyLabel = selectedProperties.length === 0
        ? t('All Portfolio')
        : (concessionaireName || t('Property'));

    return (
        <div ref={containerRef} className="v3-spatial">
            {/* Sticky stage — stays in view while user scrolls the tall container.
                Pure CSS sticky positioning; no GSAP DOM mutation. */}
            <div className="v3-spatial-stage">
                <canvas ref={canvasRef} className="v3-spatial-canvas" />

                {!loaderGone && (
                    <div className={`v3-spatial-loader ${loaderReady ? 'is-exiting' : ''}`}>
                        <img src={egoalLogo} alt="EGoal" className="v3-spatial-loader-img" />
                        <div className="v3-spatial-loader-line">
                            <div className="v3-spatial-loader-fill" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                )}

                <div ref={overlayRef} className={`v3-spatial-overlay ${revealed ? 'is-revealed' : ''} ${cardsRevealed ? 'is-cards-revealed' : ''} ${scrollProgress > 0.92 ? 'is-end-revealed' : ''}`}>
                {/* 1. Title */}
                <div className="v3-sp-title">
                    <div className="v3-sp-title-inner">
                        EGoal <span className="light">{t('Portfolio Intelligence')}</span>
                        <span className="sep"> – </span>
                        <span className="accent">"{concessionaireName || t('Mall Operator')}"</span>
                    </div>
                </div>

                {/* 1b. Top-right escape hatch — sidebar is hidden on this page */}
                <div className="v3-sp-actions-topright" role="navigation" aria-label={t('Spatial page actions')}>
                    {/* EGoal Rover — a separate product, so it opens in its own
                        tab rather than navigating away from the brief. */}
                    <button
                        type="button"
                        className="v3-sp-pill rover"
                        onClick={() => openExternalApp(ROVER_URL)}
                        title={t('Open EGoal Rover in a new tab')}
                    >
                        <ExternalLink size={13} />
                        <span>{t('EGoal Rover')}</span>
                    </button>
                    <button
                        type="button"
                        className="v3-sp-pill primary"
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/dashboard`)}
                        title={t('Smart Dashboard (D)')}
                    >
                        <LayoutDashboard size={14} />
                        <span>{t('Smart Dashboard')}</span>
                    </button>
                    <span className="v3-sp-pill-sep" />
                    <button
                        type="button"
                        className="v3-sp-pill ghost"
                        onClick={logout}
                        aria-label={t('Logout')}
                        title={t('Logout')}
                    >
                        <LogOut size={14} />
                    </button>
                </div>

                {/* 2. Top stats + property switcher */}
                <div className="v3-sp-center">
                    <div className="v3-sp-stats">
                        <div className="v3-sp-stat">
                            <Building2 size={26} strokeWidth={1.5} className="v3-sp-stat-icon" />
                            <div>
                                <span className="v3-sp-stat-val">{data.topStats.masterProjects}</span>
                                <span className="v3-sp-stat-lbl">{data.topStats.masterProjects === 1 ? t('Master Project') : t('Master Projects')}</span>
                            </div>
                        </div>
                        <div className="v3-sp-stats-divider" />
                        <div className="v3-sp-stat">
                            <Store size={26} strokeWidth={1.5} className="v3-sp-stat-icon" />
                            <div>
                                <span className="v3-sp-stat-val">{data.topStats.retailUnits}</span>
                                <span className="v3-sp-stat-lbl">{t('Retail Units')}</span>
                            </div>
                        </div>
                        <div className="v3-sp-stats-divider" />
                        <div className="v3-sp-stat">
                            <Maximize size={26} strokeWidth={1.5} className="v3-sp-stat-icon" />
                            <div>
                                <span className="v3-sp-stat-val">{data.topStats.gla}</span>
                                <span className="v3-sp-stat-lbl">{t('sq. ft. GLA')}</span>
                            </div>
                        </div>
                        <div className="v3-sp-stats-divider" />
                        <div className="v3-sp-stat">
                            <Users size={26} strokeWidth={1.5} className="v3-sp-stat-icon" />
                            <div>
                                <span className="v3-sp-stat-val">{data.topStats.occupancy}</span>
                                <span className="v3-sp-stat-lbl">{t('Occupancy')}</span>
                            </div>
                        </div>
                    </div>

                    <div className="v3-sp-period-note">
                        {t('Showing')} <strong>{t('YTD')}</strong> {t('(Jan 1 – today).')} {t('Exceptions')}: {t('Risk = 12M · Lease = 30D ahead · Forecast = year-end · Alerts = all open.')}
                    </div>

                    <PropertyFilter
                        value={selectedProperties}
                        onChange={setSelectedProperties}
                        className="v3-pf-spatial"
                    />
                </div>

                {/* 3. Left column — Sales / Footfall / Loyalty */}
                <div className="v3-sp-col v3-sp-col-left">
                    {/* Sales */}
                    <div className="v3-sp-card blue" onClick={() => setSalesPopup(true)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setSalesPopup(true)}>
                        <h3>{t('Commercial Performance')}</h3>
                        <div className="v3-sp-row" style={{ marginBottom: 4 }}>
                            <div className="v3-sp-bigval">{data.sales.revenue}</div>
                            {data.sales.growthIsUp !== null && (
                                <span className={data.sales.growthIsUp ? 'v3-sp-delta-up' : 'v3-sp-delta-down'}>
                                    {data.sales.growthIsUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                    {data.sales.growth}
                                </span>
                            )}
                        </div>
                        <div className="v3-sp-row" style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 4 }}>
                            <span>{t('Portfolio Yield')}</span>
                            <span>{t('Growth MoM')}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(147,197,253,0.9)', fontWeight: 700, marginBottom: 8 }}>
                            {t('Turnover Rent Accrual')} — {data.sales.revenueShare}
                        </div>
                        <div className="v3-sp-spark">
                            <svg width="100%" height="100%" viewBox="0 0 100 30" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="sp-blue-grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="rgba(59,130,246,0.5)" />
                                        <stop offset="100%" stopColor="rgba(59,130,246,0)" />
                                    </linearGradient>
                                </defs>
                                <path d="M0,25 Q20,15 40,20 T80,10 T100,5 L100,30 L0,30 Z" fill="url(#sp-blue-grad)" />
                                <path d="M0,25 Q20,15 40,20 T80,10 T100,5" fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </div>
                        <ul className="v3-sp-list">
                            <li>{t('Avg. Transaction')}: <span className="strong">{data.sales.avgBasket}</span></li>
                            <li>{t('Top Performer')}: <span className="strong">{data.sales.topRegion}</span></li>
                            <li>{t('Revenue Share')}: <span className="strong">{data.sales.revenueShare}</span></li>
                        </ul>
                    </div>

                    {/* Footfall */}
                    <div className="v3-sp-card purple" onClick={() => setFootfallPopup(true)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setFootfallPopup(true)}>
                        <h3>{t('Footfall & Destination')}</h3>
                        <div style={{ fontSize: 11, marginBottom: 8 }}>
                            <div className="v3-sp-row"><span style={{ color: '#cbd5e1' }}>{t('Total Footfall')}:</span><span style={{ fontWeight: 700, color: '#fff' }}>{data.capture.vehicleCount}</span></div>
                            <div className="v3-sp-row"><span style={{ color: '#cbd5e1' }}>{t('Conversion Rate')}:</span><span style={{ fontWeight: 700, color: '#d8b4fe' }}>{data.capture.rate}</span></div>
                            <div className="v3-sp-row"><span style={{ color: '#cbd5e1' }}>{t('Peak Hour')}:</span><span style={{ fontWeight: 700, color: '#d8b4fe' }}>{data.capture.peakHour}</span></div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <svg width="24" height="64" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <polygon points="0,0 100,0 80,30 20,30" fill="#60a5fa" opacity="0.85" stroke="#3b82f6" strokeWidth="1" />
                                <polygon points="20,35 80,35 60,65 40,65" fill="#a855f7" opacity="0.85" stroke="#9333ea" strokeWidth="1" />
                                <polygon points="40,70 60,70 50,100 50,100" fill="#38bdf8" opacity="0.85" stroke="#0ea5e9" strokeWidth="1" />
                            </svg>
                            <div style={{ flex: 1, fontSize: 10, color: '#cbd5e1' }}>
                                <div style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', paddingBottom: 2, marginBottom: 4 }}>
                                    <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: 9, letterSpacing: '0.05em' }}>{t('TOP')}</span> {t('Visitors enter via main lobby')}
                                </div>
                                <div style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', paddingBottom: 2, marginBottom: 4 }}>
                                    <span style={{ color: '#a855f7', fontWeight: 700, fontSize: 9, letterSpacing: '0.05em' }}>{t('MID')}</span> {t('Stayed >60 mins')}
                                </div>
                            </div>
                        </div>
                        <div className="v3-sp-row" style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(51,65,85,0.5)' }}>
                            <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Shopper Yield')}</span>
                            <span style={{ fontSize: 12, color: '#34d399', fontWeight: 700 }}>{data.capture.shopperYield}</span>
                        </div>
                    </div>

                    {/* Loyalty */}
                    <div className="v3-sp-card teal">
                        <h3>{t('Loyalty & Mobility')}</h3>
                        <div className="v3-sp-row" style={{ alignItems: 'flex-start' }}>
                            <div style={{ fontSize: 11, color: '#e2e8f0' }}>
                                <div>{t('Active Tenants')}: <span style={{ color: '#5eead4', fontWeight: 700 }}>{data.loyalty.activeUsers}</span></div>
                                <div>{t('Total Transactions')}: <span style={{ color: '#5eead4', fontWeight: 700 }}>{data.loyalty.repeatRate}</span></div>
                            </div>
                            <svg width="56" height="40" viewBox="0 0 100 80" style={{ opacity: 0.85 }}>
                                <path d="M70,10 Q85,5 90,20 Q95,40 85,55 Q70,75 50,70 Q25,60 10,40 Q5,20 20,15 Q40,5 70,10 Z" fill="#334155" stroke="#475569" strokeWidth="1" />
                                <circle cx="65" cy="25" r="3" fill="#2dd4bf" />
                                <circle cx="45" cy="55" r="3" fill="#2dd4bf" />
                                <path d="M65,25 Q50,40 45,55" fill="none" stroke="#2dd4bf" strokeWidth="1.5" strokeDasharray="3 3" />
                            </svg>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                            {propertyLabel}
                        </div>
                    </div>
                </div>

                {/* 4. Right column — Category / Risk / AI */}
                <div className="v3-sp-col v3-sp-col-right">
                    {/* Category Intelligence */}
                    <div className="v3-sp-card orange" onClick={() => setCategoryPopup(true)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setCategoryPopup(true)}>
                        <h3>{t('Category Intelligence')}</h3>

                        <div className="v3-sp-cat-section-label">{t('Top Categories')}</div>
                        <div className="v3-sp-cat-list">
                            {data.category.tops.map((t, i) => (
                                <div key={i} className="v3-sp-cat-row">
                                    <span className="v3-sp-cat-dot" style={{ background: ['#fb923c','#fdba74','#fde68a'][i] || '#fde68a' }} />
                                    <span className="v3-sp-cat-label">{t.label}</span>
                                    <span className="v3-sp-cat-pct">{t.pct}</span>
                                    {t.tenantCount != null && <span className="v3-sp-cat-meta">· {t.tenantCount}t</span>}
                                    <span className="v3-sp-cat-bar"><span style={{ width: `${t.sharePctNum}%` }} /></span>
                                </div>
                            ))}
                        </div>

                        {data.category.topSub && (
                            <div className="v3-sp-cat-sub-line">
                                ↳ {t('Top in {{parent}}', { parent: data.category.topSub.parent })}: <strong>{data.category.topSub.label}</strong>
                                {data.category.topSub.pct && <span className="v3-sp-cat-meta"> ({data.category.topSub.pct})</span>}
                            </div>
                        )}

                        {data.category.synergy && (
                            <div className="v3-sp-cat-synergy">
                                <div className="v3-sp-cat-section-label">{t('Often bought together')}</div>
                                <div className="v3-sp-synergy-chip">
                                    <span>{data.category.synergy.a}</span>
                                    <span className="v3-sp-synergy-arrow">⇄</span>
                                    <span>{data.category.synergy.b}</span>
                                    {data.category.synergy.r && <span className="v3-sp-synergy-r">r={data.category.synergy.r}</span>}
                                </div>
                            </div>
                        )}

                        {data.category.fbSplit && (
                            <div className="v3-sp-cat-fb-split">
                                <div className="v3-sp-cat-fb-text">{t('F&B')}: {t('Dine-in')} <strong>{data.category.fbSplit.dineIn}%</strong> · {t('Takeaway')} <strong>{data.category.fbSplit.takeaway}%</strong></div>
                                <div className="v3-sp-cat-fb-bar">
                                    <span className="dine" style={{ width: `${data.category.fbSplit.dineIn}%` }} />
                                    <span className="take" style={{ width: `${data.category.fbSplit.takeaway}%` }} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Revenue Risk */}
                    <div className="v3-sp-card rose" onClick={() => setRiskPopup(true)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setRiskPopup(true)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                            <h3 style={{ color: '#fecdd3', margin: 0 }}>{t('Revenue Risk Indicators')}</h3>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f43f5e', boxShadow: '0 0 8px rgba(244,63,94,0.8)' }} />
                        </div>
                        <div className="v3-sp-risk-metric">
                            <div className="v3-sp-risk-icon-wrap"><TrendingDown size={14} /></div>
                            <div>
                                <div className="v3-sp-risk-title">{t('Top Risk Tenant')}</div>
                                <div className="v3-sp-risk-sub">{data.risk.locDeclineLabel} — <span className="v3-sp-risk-strong">{data.risk.locDeclineValue}</span></div>
                            </div>
                        </div>
                        <div className="v3-sp-risk-metric">
                            <div className="v3-sp-risk-icon-wrap"><Coffee size={14} /></div>
                            <div>
                                <div className="v3-sp-risk-title">{t('Watch List')} ({data.risk.watchCount})</div>
                                <div className="v3-sp-risk-sub">{t('At-risk annual revenue')}: <span className="v3-sp-risk-strong">{data.risk.atRiskRevenue}</span></div>
                            </div>
                        </div>
                        <div className="v3-sp-risk-metric">
                            <div className="v3-sp-risk-icon-wrap"><Clock size={14} /></div>
                            <div>
                                <div className="v3-sp-risk-title">{t('Lease Expiry Alert')}</div>
                                <div className="v3-sp-risk-sub">{t('{{count}} stores', { count: data.risk.leaseStores })} <span className="v3-sp-risk-strong">({t('in {{days}} days', { days: data.risk.leaseDays })})</span></div>
                            </div>
                        </div>
                    </div>

                    {/* AI Mall Brain Insights */}
                    <div className="v3-sp-card blue v3-ai-card">
                        <div className="v3-ai-sheen" aria-hidden="true" />
                        <div className="v3-ai-head">
                            <span className="v3-ai-badge"><Sparkles size={13} /></span>
                            <h3 style={{ margin: 0 }}>{t('AI Mall Brain')}</h3>
                            <span className="v3-ai-live" aria-hidden="true" />
                            <span className="v3-ai-live-txt">{t('LIVE')}</span>
                        </div>

                        {(data.ai.insights.length > 0 || data.ai.alerts.length > 0) ? (
                            <>
                                <div className="v3-ai-sub">
                                    <Activity size={10} />
                                    {t('Synthesized from {{count}} signals', { count: data.ai.insights.length + data.ai.alerts.length })}
                                </div>

                                <div className="v3-ai-insights">
                                    {data.ai.insights.slice(0, 2).map((it, i) => (
                                        <div key={i} className={`v3-ai-insight ${it.direction}`}>
                                            <div className="v3-ai-ins-head">
                                                <span className="v3-ai-ins-name">{it.name}</span>
                                                {it.metric && (
                                                    <span className={`v3-ai-pill ${it.direction}`}>
                                                        {it.direction === 'down' ? <ArrowDownRight size={11} /> : it.direction === 'up' ? <ArrowUpRight size={11} /> : null}
                                                        {it.metric}
                                                    </span>
                                                )}
                                            </div>
                                            {it.body && <div className="v3-ai-body">{it.body}</div>}
                                            {it.recommendation && (
                                                <div className="v3-ai-rec">
                                                    <Lightbulb size={11} />
                                                    <span>{it.recommendation}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {data.ai.alerts.length > 0 && (
                                    <div className="v3-ai-signals">
                                        {data.ai.alerts.slice(0, 3).map((a, i) => (
                                            <span key={i} className={`v3-ai-chip ${a.tone}`} title={a.full}>
                                                {a.tone === 'danger' ? <AlertTriangle size={10} /> : <Activity size={10} />}
                                                {a.name && <strong>{a.name}</strong>}
                                                <span className="v3-ai-chip-txt">{a.detail}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="v3-ai-empty">
                                <Sparkles size={13} />
                                {t('Insights generating — check back after the next scheduled run.')}
                            </div>
                        )}
                    </div>
                </div>

                {/* 5. Bottom logo */}
                <div className="v3-sp-logo-bottom">
                    <span className="brand">EGoal</span>
                </div>

                {/* 6. End-of-scroll CTA — appears when user reaches the last frame */}
                <button
                    type="button"
                    className="v3-sp-end-cta"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/dashboard`)}
                >
                    <LayoutDashboard size={18} />
                    <span>{t('Enter Smart Dashboard')}</span>
                    <span className="v3-sp-end-cta-arrow">→</span>
                </button>

                {loadError && (
                    <div style={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', color: '#fca5a5', fontSize: 12, background: 'rgba(76,5,25,0.6)', padding: '6px 12px', borderRadius: 8 }}>
                        {t('Brief unavailable')}: {loadError}
                    </div>
                )}
                </div>
            </div>

            {/* Commercial Performance detail popup */}
            {salesPopup && <SalesDetailPopup brief={brief} onClose={() => setSalesPopup(false)} />}
            {/* Category Intelligence detail popup */}
            {categoryPopup && <CategoryDetailPopup brief={brief} onClose={() => setCategoryPopup(false)} />}
            {/* Footfall & Destination detail popup */}
            {footfallPopup && <FootfallDetailPopup brief={brief} onClose={() => setFootfallPopup(false)} />}
            {/* Revenue Risk detail popup */}
            {riskPopup && <RiskDetailPopup brief={brief} onClose={() => setRiskPopup(false)} />}
        </div>
    );
}

/* ───────────────────── Sales popup ───────────────────── */

function SalesDetailPopup({ brief, onClose }) {
    const { t } = useTranslation();
    const sales = brief?.sales || {};
    const performers = Array.isArray(sales.top_performers) ? sales.top_performers : [];
    const growthPct = typeof sales.growth_pct === 'number' ? sales.growth_pct : null;

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="v3-sp-popup-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="v3-sp-popup" onClick={e => e.stopPropagation()}>
                <div className="v3-sp-popup-header">
                    <h2>{t('Commercial Performance')}</h2>
                    <button className="v3-sp-popup-close" onClick={onClose} aria-label={t('Close')}><X size={18} /></button>
                </div>

                {/* KPI row */}
                <div className="v3-sp-popup-kpis">
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Total Revenue (YTD)')}</span>
                        <span className="v3-sp-popup-kpi-val blue">{sales.total != null ? formatMoney(sales.total) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Growth vs SPLY')}</span>
                        <span className={`v3-sp-popup-kpi-val ${growthPct == null ? '' : growthPct >= 0 ? 'green' : 'red'}`}>
                            {growthPct != null ? formatPct(growthPct) : '—'}
                        </span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Avg. Transaction')}</span>
                        <span className="v3-sp-popup-kpi-val">{sales.atv != null ? formatMoney(sales.atv) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Revenue Share')}</span>
                        <span className="v3-sp-popup-kpi-val blue">{sales.revenue_share != null ? formatMoney(sales.revenue_share) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Transactions (YTD)')}</span>
                        <span className="v3-sp-popup-kpi-val">{sales.total_transactions != null ? formatNumber(sales.total_transactions) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Prior Year Sales')}</span>
                        <span className="v3-sp-popup-kpi-val">{sales.prev_total != null ? formatMoney(sales.prev_total) : '—'}</span>
                    </div>
                </div>

                {/* Top performers table */}
                {performers.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title">{t('Top Performers')}</h3>
                        <table className="v3-sp-popup-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('Tenant')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Sales')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Revenue Share')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Transactions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performers.map((p, i) => (
                                    <tr key={p.tenantId || i}>
                                        <td>{i + 1}</td>
                                        <td>{p.tenantName || p.name || '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(p.sales)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(p.revenue)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(p.transactions)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
}

/* ───────────────────── Footfall popup ───────────────────── */

function FootfallDetailPopup({ brief, onClose }) {
    const { t } = useTranslation();
    const ff = brief?.footfall || {};
    const loyalty = brief?.loyalty || {};

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const netRetention = ff.total && ff.total_exits != null
        ? ((ff.total - ff.total_exits) / ff.total * 100)
        : null;

    return (
        <div className="v3-sp-popup-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="v3-sp-popup v3-sp-popup-purple" onClick={e => e.stopPropagation()}>
                <div className="v3-sp-popup-header">
                    <h2>{t('Footfall & Destination')}</h2>
                    <button className="v3-sp-popup-close" onClick={onClose} aria-label={t('Close')}><X size={18} /></button>
                </div>

                {/* KPI grid */}
                <div className="v3-sp-popup-kpis">
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Total Entries (YTD)')}</span>
                        <span className="v3-sp-popup-kpi-val purple">{ff.total != null ? formatNumber(ff.total) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Total Exits (YTD)')}</span>
                        <span className="v3-sp-popup-kpi-val">{ff.total_exits != null ? formatNumber(ff.total_exits) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Conversion Rate')}</span>
                        <span className="v3-sp-popup-kpi-val purple">{ff.conversion_rate != null ? `${Number(ff.conversion_rate).toFixed(2)}%` : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Peak Hour')}</span>
                        <span className="v3-sp-popup-kpi-val">{ff.peak_hour != null ? `${ff.peak_hour}:00` : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Peak Hour Avg')}</span>
                        <span className="v3-sp-popup-kpi-val">{ff.peak_hour_avg != null ? formatNumber(ff.peak_hour_avg, 0) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Shopper Yield')}</span>
                        <span className="v3-sp-popup-kpi-val green">{ff.shopper_yield != null ? formatMoney(ff.shopper_yield) : '—'}</span>
                    </div>
                </div>

                {/* Derived metrics */}
                <h3 className="v3-sp-popup-section-title">{t('Derived Metrics')}</h3>
                <div className="v3-sp-popup-kpis" style={{ marginBottom: 0 }}>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Total Revenue')}</span>
                        <span className="v3-sp-popup-kpi-val">{ff.revenue != null ? formatMoney(ff.revenue) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Transactions')}</span>
                        <span className="v3-sp-popup-kpi-val">{ff.transactions != null ? formatNumber(ff.transactions) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Net Retention')}</span>
                        <span className={`v3-sp-popup-kpi-val ${netRetention != null && netRetention > 0 ? 'green' : ''}`}>
                            {netRetention != null ? `${netRetention.toFixed(1)}%` : '—'}
                        </span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Active Tenants')}</span>
                        <span className="v3-sp-popup-kpi-val">{loyalty.active_tenants != null ? formatNumber(loyalty.active_tenants) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Footfall per Tenant')}</span>
                        <span className="v3-sp-popup-kpi-val">
                            {ff.total != null && loyalty.active_tenants
                                ? formatNumber(Math.round(ff.total / loyalty.active_tenants))
                                : '—'}
                        </span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Txn per Visitor')}</span>
                        <span className="v3-sp-popup-kpi-val">
                            {ff.transactions != null && ff.total
                                ? (ff.transactions / ff.total).toFixed(3)
                                : '—'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ───────────────────── Risk popup ───────────────────── */

const RISK_TIER_COLORS = { danger: '#f43f5e', watch: '#f59e0b', healthy: '#34d399' };
const RISK_TIER_LABELS = { danger: 'Danger', watch: 'Watch', healthy: 'Healthy' };

function RiskDetailPopup({ brief, onClose }) {
    const { t } = useTranslation();
    const risk = brief?.revenue_risk || {};
    const allRisks = Array.isArray(risk.all_risks) ? risk.all_risks : [];
    const leases = Array.isArray(risk.lease_expiries) ? risk.lease_expiries : [];

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="v3-sp-popup-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="v3-sp-popup v3-sp-popup-rose" onClick={e => e.stopPropagation()}>
                <div className="v3-sp-popup-header">
                    <h2>{t('Revenue Risk Indicators')}</h2>
                    <button className="v3-sp-popup-close" onClick={onClose} aria-label={t('Close')}><X size={18} /></button>
                </div>

                {/* KPI grid */}
                <div className="v3-sp-popup-kpis">
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Danger')}</span>
                        <span className="v3-sp-popup-kpi-val rose">{risk.danger_count ?? 0}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Watch')}</span>
                        <span className="v3-sp-popup-kpi-val amber">{risk.watch_count ?? 0}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Healthy')}</span>
                        <span className="v3-sp-popup-kpi-val green">{risk.healthy_count ?? 0}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('At-Risk Revenue')}</span>
                        <span className="v3-sp-popup-kpi-val rose">{risk.at_risk_revenue != null ? formatMoney(risk.at_risk_revenue) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Lease Expiries (30d)')}</span>
                        <span className="v3-sp-popup-kpi-val amber">{leases.length}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Tenants Evaluated')}</span>
                        <span className="v3-sp-popup-kpi-val">{(risk.danger_count ?? 0) + (risk.watch_count ?? 0) + (risk.healthy_count ?? 0)}</span>
                    </div>
                </div>

                {/* Flight risk table */}
                {allRisks.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title">{t('Flight Risk Rankings')}</h3>
                        <table className="v3-sp-popup-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('Tenant')}</th>
                                    <th style={{ textAlign: 'center' }}>{t('Tier')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Score')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('OCR')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('12M Sales')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allRisks.map((r, i) => (
                                    <tr key={r.tenant_id || i}>
                                        <td>{i + 1}</td>
                                        <td>{r.tenant_name || '—'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className="v3-sp-tier-badge" style={{ background: `${RISK_TIER_COLORS[r.risk_tier] || '#64748b'}22`, color: RISK_TIER_COLORS[r.risk_tier] || '#64748b' }}>
                                                {t(RISK_TIER_LABELS[r.risk_tier] || r.risk_tier)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.composite_score != null ? r.composite_score.toFixed(2) : '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{r.ocr != null ? `${(r.ocr * 100).toFixed(1)}%` : '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{r.total_12m_sales != null ? formatMoney(r.total_12m_sales) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {/* Lease expiry table */}
                {leases.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title" style={{ marginTop: 20 }}>{t('Upcoming Lease Expiries')}</h3>
                        <table className="v3-sp-popup-table">
                            <thead>
                                <tr>
                                    <th>{t('Tenant')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Days Left')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Base Rent')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leases.map((l, i) => (
                                    <tr key={l.tenantId || i}>
                                        <td>{l.tenantName || '—'}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span className="v3-sp-tier-badge" style={{ background: l.daysRemaining <= 7 ? 'rgba(244,63,94,0.15)' : 'rgba(245,158,11,0.15)', color: l.daysRemaining <= 7 ? '#f43f5e' : '#f59e0b' }}>
                                                {l.daysRemaining}d
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{l.baseRent != null ? formatMoney(l.baseRent) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
}

/* ───────────────────── Category popup ───────────────────── */

const CAT_COLORS = ['#f97316','#fb923c','#fdba74','#fde68a','#fef3c7','#d4d4d8','#a1a1aa','#71717a','#52525b','#3f3f46'];

function CategoryDetailPopup({ brief, onClose }) {
    const { t } = useTranslation();
    const cat = brief?.category_intel || {};
    const categories = Array.isArray(cat.all_categories) ? cat.all_categories : [];
    const subcategories = cat.subcategories && typeof cat.subcategories === 'object' ? cat.subcategories : {};
    const synergy = Array.isArray(cat.all_synergy_pairs) ? cat.all_synergy_pairs : [];
    const grandTotal = categories.reduce((s, c) => s + (c.totalSales || 0), 0) || 1;
    const [expandedCat, setExpandedCat] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="v3-sp-popup-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="v3-sp-popup v3-sp-popup-orange" onClick={e => e.stopPropagation()}>
                <div className="v3-sp-popup-header">
                    <h2>{t('Category Intelligence')}</h2>
                    <button className="v3-sp-popup-close" onClick={onClose} aria-label={t('Close')}><X size={18} /></button>
                </div>

                {/* KPI row */}
                <div className="v3-sp-popup-kpis">
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Categories')}</span>
                        <span className="v3-sp-popup-kpi-val orange">{categories.length}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Projected Year-End')}</span>
                        <span className="v3-sp-popup-kpi-val orange">{cat.projected_year_end != null ? formatMoney(cat.projected_year_end) : '—'}</span>
                    </div>
                    <div className="v3-sp-popup-kpi">
                        <span className="v3-sp-popup-kpi-label">{t('Day-Part Peak')}</span>
                        <span className="v3-sp-popup-kpi-val">{cat.fb_split?.peakLabel || '—'}</span>
                    </div>
                </div>

                {/* Horizontal bar chart */}
                {categories.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title">{t('Category Breakdown')}</h3>
                        <div className="v3-sp-cat-bars">
                            {categories.map((c, i) => {
                                const pct = (c.totalSales || 0) / grandTotal * 100;
                                return (
                                    <div key={c.category || i} className="v3-sp-cat-bar-row">
                                        <div className="v3-sp-cat-bar-label">{c.category || '—'}</div>
                                        <div className="v3-sp-cat-bar-track">
                                            <div className="v3-sp-cat-bar-fill" style={{ width: `${pct}%`, background: CAT_COLORS[i] || CAT_COLORS[CAT_COLORS.length - 1] }} />
                                        </div>
                                        <div className="v3-sp-cat-bar-val">{formatMoney(c.totalSales)}</div>
                                        <div className="v3-sp-cat-bar-pct">{pct.toFixed(1)}%</div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Category table */}
                {categories.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title" style={{ marginTop: 20 }}>{t('Detail')}</h3>
                        <table className="v3-sp-popup-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('Category')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Sales')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Share')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Tenants')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((c, i) => (
                                    <tr key={c.category || i}>
                                        <td>{i + 1}</td>
                                        <td>
                                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: CAT_COLORS[i] || CAT_COLORS[CAT_COLORS.length - 1], marginRight: 6, verticalAlign: 'middle' }} />
                                            {c.category || '—'}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(c.totalSales)}</td>
                                        <td style={{ textAlign: 'right' }}>{c.sharePct != null ? `${c.sharePct.toFixed(1)}%` : '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{c.tenantCount ?? '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {/* Subcategory Analysis */}
                {Object.keys(subcategories).length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title" style={{ marginTop: 20 }}>{t('Subcategory Analysis')}</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {categories.map((c, ci) => {
                                const subs = subcategories[c.category] || [];
                                if (!subs.length) return null;
                                const isOpen = expandedCat === c.category;
                                return (
                                    <div key={c.category || ci} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                                        <button
                                            type="button"
                                            onClick={() => setExpandedCat(isOpen ? null : c.category)}
                                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'transparent', border: 'none', color: '#e2e8f0', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: CAT_COLORS[ci] || CAT_COLORS[CAT_COLORS.length - 1], flexShrink: 0 }} />
                                                <span style={{ fontWeight: 600 }}>{c.category}</span>
                                                <span style={{ color: '#94a3b8', fontSize: 11 }}>({t('{{count}} subcategories', { count: subs.length })})</span>
                                            </span>
                                            <span style={{ color: '#94a3b8', fontSize: 14, transition: 'transform 200ms', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                                        </button>
                                        {isOpen && (
                                            <div style={{ padding: '0 12px 10px' }}>
                                                <table className="v3-sp-popup-table" style={{ fontSize: 11 }}>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('Subcategory')}</th>
                                                            <th style={{ textAlign: 'right' }}>{t('Sales')}</th>
                                                            <th style={{ textAlign: 'right' }}>{t('Share')}</th>
                                                            <th style={{ textAlign: 'right' }}>{t('Tenants')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {subs.map((s, si) => (
                                                            <tr key={s.subcategory || si}>
                                                                <td>{s.subcategory || '—'}</td>
                                                                <td style={{ textAlign: 'right' }}>{formatMoney(s.totalSales)}</td>
                                                                <td style={{ textAlign: 'right' }}>{s.sharePct != null ? `${s.sharePct.toFixed(1)}%` : '—'}</td>
                                                                <td style={{ textAlign: 'right' }}>{s.tenantCount ?? '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Synergy pairs */}
                {synergy.length > 0 && (
                    <>
                        <h3 className="v3-sp-popup-section-title" style={{ marginTop: 20 }}>{t('Synergy Pairs')}</h3>
                        <table className="v3-sp-popup-table">
                            <thead>
                                <tr>
                                    <th>{t('Category A')}</th>
                                    <th>{t('Category B')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Correlation')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {synergy.map((p, i) => (
                                    <tr key={i}>
                                        <td>{p.a}</td>
                                        <td>{p.b}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span className="v3-sp-corr-badge">{Number(p.correlation).toFixed(2)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
}

/* ───────────────────── helpers ───────────────────── */

// Parse the LLM "AI Mall Brain" narrative (markdown-ish, "•"-separated bullets)
// into structured insight cards: { name, metric, body, recommendation, direction }.
// Degrades gracefully — unrecognised text becomes a single neutral insight.
function parseAIInsights(text) {
    if (!text || typeof text !== 'string') return [];
    const chunks = text.split('•').map(s => s.trim()).filter(Boolean);
    return (chunks.length ? chunks : [text]).map(raw => {
        const chunk = raw.trim();
        // Headline = first **bold** span; strip markdown asterisks from the rest.
        const boldMatch = chunk.match(/\*\*(.+?)\*\*/);
        const headline = boldMatch ? boldMatch[1].trim() : '';
        let rest = (boldMatch ? chunk.slice(boldMatch.index + boldMatch[0].length) : chunk)
            .replace(/\*\*/g, '').replace(/^[\s—–-]+/, '').trim();
        // Pull a recommendation sentence ("Consider…", "Recommend…", "Suggest…").
        let recommendation = null;
        const recMatch = rest.match(/\b(?:consider|recommend|suggest)\b.*$/i);
        if (recMatch) {
            recommendation = recMatch[0].trim();
            rest = rest.slice(0, recMatch.index).trim().replace(/[.\s]+$/, '');
        }
        // Split headline into name + metric ("Tenant Name: +12.3% LFL").
        let name = headline, metric = null;
        const hm = headline.match(/^(.*?):\s*([+-−]?\d.*)$/);
        if (hm) { name = hm[1].trim(); metric = hm[2].trim(); }
        // Direction from the metric sign, then perf wording as a fallback.
        let direction = 'neutral';
        if (metric && /^[-−]/.test(metric)) direction = 'down';
        else if (metric && /^\+/.test(metric)) direction = 'up';
        else if (/\bunderperform|declin|down\b/i.test(rest)) direction = 'down';
        else if (/\boutperform|grow|up\b/i.test(rest)) direction = 'up';
        return {
            name: name || headline || chunk.replace(/\*\*/g, '').slice(0, 60),
            metric,
            body: rest || null,
            recommendation,
            direction,
        };
    }).filter(it => it.name);
}

// Classify an active-alert string into a tinted "signal" chip.
function classifyAlert(str) {
    const full = String(str || '').trim();
    let tone = 'anomaly';
    if (/danger|critical|severe/i.test(full)) tone = 'danger';
    else if (/σ|sigma|anomal|down|drop|spike|decline|fell/i.test(full)) tone = 'anomaly';
    else tone = 'info';
    let name = null, detail = full;
    const colon = full.indexOf(':');
    if (colon > 0 && colon < 28) {
        name = full.slice(0, colon).trim();
        detail = full.slice(colon + 1).trim();
    } else {
        const vm = full.match(/^(.+?)\s+(?:entered|is|has|dropped|down|spiked|fell|declined)\b/i);
        if (vm) { name = vm[1].trim(); detail = full.slice(vm[1].length).trim(); }
    }
    return { name, detail, tone, full };
}

// Map our /api/spatial/brief response into the shape the overlay expects.
// Anything we don't have a real value for becomes a clearly-marked placeholder.
function deriveData(brief, t) {
    const portfolio = brief?.portfolio || {};
    const sales = brief?.sales || {};
    const ff = brief?.footfall || {};
    const loy = brief?.loyalty || {};
    const cat = brief?.category_intel || {};
    const risk = brief?.revenue_risk || {};
    const ai = brief?.ai_insights || {};

    const growthPct = typeof sales.growth_pct === 'number' ? sales.growth_pct : null;
    const tops = Array.isArray(cat.top_categories) ? cat.top_categories : [];
    const totalCat = tops.reduce((s, c) => s + (c?.totalSales || c?.sales || 0), 0) || 1;
    const topsNorm = tops.slice(0, 3).map(c => ({
        label: c?.category || c?.name || PLACEHOLDER,
        pct: ((c?.totalSales || c?.sales || 0) / totalCat * 100).toFixed(0) + '%',
        share: (c?.totalSales || c?.sales || 0) / totalCat,
    }));
    while (topsNorm.length < 3) topsNorm.push({ label: PLACEHOLDER, pct: PLACEHOLDER, share: 0 });

    const subMap = cat.subcategories && typeof cat.subcategories === 'object' ? cat.subcategories : {};
    const topCatName = tops[0]?.category || tops[0]?.name;
    const topSubArr = topCatName ? subMap[topCatName] : null;
    const topSub = Array.isArray(topSubArr) && topSubArr.length
        ? [...topSubArr].sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))[0]
        : null;
    const synergyArr = Array.isArray(cat.synergy_pairs) ? cat.synergy_pairs : [];
    const topPair = synergyArr.length
        ? [...synergyArr].sort((a, b) => Math.abs(b.correlation || 0) - Math.abs(a.correlation || 0))[0]
        : null;
    const fb = cat.fb_split || {};

    const topRisk = Array.isArray(risk.top_risks) ? risk.top_risks[0] : null;
    const leaseList = Array.isArray(risk.lease_expiries) ? risk.lease_expiries : [];

    const aiText = typeof ai.text === 'string' ? ai.text : '';
    const aiAlerts = Array.isArray(ai.active_alerts) ? ai.active_alerts.map(a => a.message || a.title || a.label).filter(Boolean) : [];

    return {
        topStats: {
            masterProjects: portfolio.property_count || PLACEHOLDER,
            retailUnits: portfolio.total_stores ? formatNumber(portfolio.total_stores) : PLACEHOLDER,
            gla: portfolio.total_gla ? formatCompactNumber(portfolio.total_gla) : PLACEHOLDER,
            occupancy: portfolio.occupancy_pct != null ? `${portfolio.occupancy_pct.toFixed(0)}%` : PLACEHOLDER,
        },
        sales: {
            revenue: sales.total != null ? formatMoney(sales.total) : PLACEHOLDER,
            growth: growthPct != null ? formatPct(growthPct) : PLACEHOLDER,
            growthIsUp: growthPct == null ? null : growthPct >= 0,
            avgBasket: sales.atv != null ? formatMoney(sales.atv) : PLACEHOLDER,
            topRegion: sales.top_performer?.name || sales.top_performer?.tenant_name || sales.top_performer?.tenantName || PLACEHOLDER,
            revenueShare: sales.revenue_share != null ? formatMoney(sales.revenue_share) : PLACEHOLDER,
        },
        capture: {
            vehicleCount: ff.total != null ? formatNumber(ff.total) : PLACEHOLDER,
            rate: ff.conversion_rate != null ? formatPct(ff.conversion_rate) : PLACEHOLDER,
            peakHour: ff.peak_hour != null ? `${ff.peak_hour}:00` : PLACEHOLDER,
            shopperYield: ff.shopper_yield != null ? formatMoney(ff.shopper_yield) : PLACEHOLDER,
        },
        loyalty: {
            activeUsers: loy.active_tenants != null ? formatNumber(loy.active_tenants) : PLACEHOLDER,
            repeatRate: loy.total_transactions != null ? formatNumber(loy.total_transactions) : PLACEHOLDER,
        },
        category: {
            tops: topsNorm
                .map((t, i) => ({
                    ...t,
                    tenantCount: tops[i]?.tenantCount ?? null,
                    sharePctNum: tops[i] ? ((tops[i].totalSales || tops[i].sales || 0) / totalCat * 100) : 0,
                }))
                .filter(t => t.label && t.label !== PLACEHOLDER),
            topSub: topSub ? {
                parent: topCatName,
                label: topSub.subcategory || topSub.category,
                pct: topSub.sharePct != null ? `${Number(topSub.sharePct).toFixed(0)}%` : null,
            } : null,
            synergy: topPair ? {
                a: topPair.a,
                b: topPair.b,
                r: typeof topPair.correlation === 'number' ? topPair.correlation.toFixed(2) : null,
            } : null,
            fbSplit: ((fb.dineInPct ?? 0) > 0 || (fb.takeawayPct ?? 0) > 0) ? {
                dineIn: Math.round(fb.dineInPct ?? 0),
                takeaway: Math.round(fb.takeawayPct ?? 0),
            } : null,
        },
        risk: {
            locDeclineLabel: topRisk?.tenant_name || topRisk?.name || PLACEHOLDER,
            locDeclineValue: topRisk?.composite_score != null
                ? t('score {{value}}', { value: Number(topRisk.composite_score).toFixed(1) })
                : (topRisk?.changePct != null ? formatPct(topRisk.changePct) : PLACEHOLDER),
            atRiskRevenue: risk.at_risk_revenue != null ? formatMoney(risk.at_risk_revenue) : PLACEHOLDER,
            watchCount: risk.watch_count || 0,
            leaseStores: leaseList.length || 0,
            leaseDays: 30,
        },
        ai: {
            insights: parseAIInsights(aiText),
            alerts: aiAlerts.map(classifyAlert),
        },
    };
}
